/**
 * /assets/mix-match.js
 * Mix & Match PDP - Dynamic product switching for swimsuit sets
 */
(function () {
  'use strict';

  const SELECTORS = {
    container: '[data-mix-match-selectors]',
    config: 'mix-match-config-',
    toggle: '[data-mm-toggle]',
    options: '[data-mm-options]',
    label: '[data-mm-label]',
    arrow: '[data-mm-arrow]',
    loading: '[data-mm-loading]',
    // Theme selectors for DOM swaps
    gallery: '#ProductPhoto--',
    priceBlock: '.product__price-and-badge',
    formWrapper: '[data-form-wrapper]',
    descriptionAccordion: '.product__block[data-block-type="product_accordion"]',
    modelInfoJson: '#variant-model-info-json',
    productWrapper: '[data-product]',
    sectionContainer: '[data-section-type="product-template"]',
    mediaSlider: '[data-product-single-media-slider]',
  };

  const CLASSES = {
    pillActive: 'mix-match__pill--active',
    pillUnavailable: 'mix-match__pill--unavailable',
    optionsVisible: 'mix-match__options--visible',
    optionsHidden: 'mix-match__options--hidden',
    loadingActive: 'mix-match__loading--active',
    switching: 'mix-match-switching',
  };

  class MixMatch {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.sectionContainer = container.closest(SELECTORS.sectionContainer);

      const configEl = document.getElementById(SELECTORS.config + this.sectionId);
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('Mix & Match: Invalid config JSON', e);
        return;
      }

      this.state = {
        currentTop: this.config.currentTop,
        currentBottom: this.config.currentBottom,
        currentHandle: this.config.currentHandle,
        isFetching: false,
      };

      this.cache = {};

      // Parse group 2 color names for swatch grouping
      this.group2Colors = new Set(
        (this.config.swatchGroup2Colors || '')
          .split('\n')
          .map(s => s.trim().toLowerCase())
          .filter(Boolean)
      );

      // Parse hidden color names
      this.hiddenColors = new Set(
        (this.config.hiddenColors || '')
          .split('\n')
          .map(s => s.trim().toLowerCase())
          .filter(Boolean)
      );

      this.init();
    }

    init() {
      this.renderPills();
      this.bindEvents();
      this.updatePillStates();
      this.markSizeSelector();
      this.groupColorSwatches();
      this.reorderBlocks();
      this.initStickyATC();
      this.initModelInfo();
      this.initArrowHint();
      this.checkHelperOverlap();
      let helperResizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(helperResizeTimer);
        helperResizeTimer = setTimeout(() => this.checkHelperOverlap(), 150);
      });

      // Render our custom reviews widget on initial load
      // The mm-reviews section provides the container — wait for DOM to be ready
      const initReviews = () => {
        try {
          const productJsonEl = document.querySelector(`#ProductJson-${this.sectionId}`);
          if (!productJsonEl) return;
          const productData = JSON.parse(productJsonEl.textContent);
          if (!productData.id) return;
          const container = document.getElementById('judgeme_product_reviews');
          if (container) {
            this.refreshJudgeMeReviews(productData.id);
          } else {
            setTimeout(initReviews, 300);
          }
        } catch (e) {}
      };
      if (document.readyState === 'complete') {
        initReviews();
      } else {
        window.addEventListener('load', initReviews);
      }
    }

    // --- Arrow hint animation ---
    initArrowHint() {
      const arrows = this.container.querySelectorAll('.mix-match__arrow');
      if (!arrows.length) return;

      const playHint = () => {
        setTimeout(() => {
          arrows.forEach(arrow => {
            // Don't animate if already expanded
            const toggle = arrow.closest('.mix-match__toggle');
            if (toggle && toggle.getAttribute('aria-expanded') === 'true') return;
            arrow.classList.add('mix-match__arrow--hint');
            arrow.addEventListener('animationend', () => {
              arrow.classList.remove('mix-match__arrow--hint');
            }, { once: true });
          });
        }, 1500);
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            playHint();
            observer.disconnect();
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -20% 0px' });

      observer.observe(this.container);
    }

    // --- Model Info Overlay ---
    initModelInfo() {
      this.modelInfoMap = this.parseModelInfoJson();
      this.modelInfoOverlay = null;

      // Bind listeners ONCE on the persistent sectionContainer. refreshModelInfo
      // does not re-bind — previously it did, which leaked listeners on every
      // product switch and caused duplicate updates.
      this._onThemeVariantChange = (e) => {
        const variant = e && e.detail && e.detail.variant;
        if (variant && variant.id != null) this.updateModelInfo(String(variant.id));
      };
      this.sectionContainer.addEventListener('theme:variant:change', this._onThemeVariantChange);

      // Fallback path: some flows update the variant `input[name="id"]` value
      // and dispatch a native change event without firing theme:variant:change.
      // Delegated listener catches those.
      this._onVariantInputChange = (e) => {
        if (e && e.target && e.target.name === 'id' && e.target.value) {
          this.updateModelInfo(e.target.value);
        }
      };
      this.sectionContainer.addEventListener('change', this._onVariantInputChange);

      // Create the overlay element and render the initial state.
      this.createModelInfoOverlay();
      this.renderCurrentVariant();
    }

    parseModelInfoJson() {
      try {
        const el = document.querySelector(SELECTORS.modelInfoJson);
        if (!el) return {};
        const raw = (el.textContent || '').trim();
        return raw ? JSON.parse(raw) : {};
      } catch (e) {
        console.warn('Mix & Match: Could not parse model info JSON', e);
        return {};
      }
    }

    createModelInfoOverlay() {
      // Idempotent — don't double-create if the previous overlay still exists.
      if (this.modelInfoOverlay && this.modelInfoOverlay.isConnected) return;

      const slider = this.sectionContainer.querySelector(SELECTORS.mediaSlider);
      if (!slider) return;

      // Mount the overlay OUTSIDE the Flickity slider entirely — Flickity
      // claims any element placed inside [data-product-single-media-slider]
      // (including inside .flickity-viewport) as a cell and applies inline
      // transforms that drag the overlay off-screen at translateX(6000%) or
      // similar. The parent gallery container (`data-product-single-media-group`
      // → `#ProductPhoto--…`) is Flickity-untouched.
      const galleryContainer = slider.closest('[data-product-single-media-group]') || slider.parentNode;
      if (!galleryContainer) return;

      if (getComputedStyle(galleryContainer).position === 'static') {
        galleryContainer.style.position = 'relative';
      }

      const overlay = document.createElement('div');
      overlay.className = 'mm-model-info';
      overlay.setAttribute('aria-hidden', 'true');
      galleryContainer.appendChild(overlay);
      this.modelInfoOverlay = overlay;

      // JS-driven positioning: align overlay to the slider's bottom-left
      // corner within the gallery container. (Container height includes the
      // thumbnails strip below the slider, so CSS `bottom: 16px` would put
      // the overlay below the thumbnails — off the visible photo entirely.)
      this.positionModelInfoOverlay();
      if (!this._modelInfoResizeBound) {
        this._modelInfoResizeBound = true;
        window.addEventListener('resize', () => this.positionModelInfoOverlay());
      }
      if (window.ResizeObserver && !this._modelInfoSliderObserver) {
        this._modelInfoSliderObserver = new ResizeObserver(() => this.positionModelInfoOverlay());
        this._modelInfoSliderObserver.observe(slider);
      }
    }

    positionModelInfoOverlay() {
      if (!this.modelInfoOverlay) return;
      const slider = this.sectionContainer.querySelector(SELECTORS.mediaSlider);
      const parent = this.modelInfoOverlay.parentElement;
      if (!slider || !parent) return;

      const parentRect = parent.getBoundingClientRect();
      const sliderRect = slider.getBoundingClientRect();
      const isMobile = window.innerWidth <= 749;
      const offset = isMobile ? 12 : 16;

      // Slider's bottom-left, plus inset. Anchor via `top` because
      // `bottom` would resolve to the bottom of the (taller) parent.
      const top = (sliderRect.top - parentRect.top) + sliderRect.height - this.modelInfoOverlay.offsetHeight - offset;
      const left = (sliderRect.left - parentRect.left) + offset;
      this.modelInfoOverlay.style.top = top + 'px';
      this.modelInfoOverlay.style.left = left + 'px';
    }

    renderCurrentVariant() {
      const variantInput = this.sectionContainer.querySelector('input[name="id"]');
      const value = variantInput && variantInput.value && String(variantInput.value).trim();
      if (value) {
        this.updateModelInfo(value);
        this._modelInfoRetries = 0;
        return;
      }
      // Variant input not hydrated yet — theme JS may set it on a later
      // tick. Retry up to 10x at 100ms intervals (≈1s total) before giving
      // up. Without this, model info silently never renders when our init
      // runs before theme.dev.js sets the variant id.
      this._modelInfoRetries = (this._modelInfoRetries || 0) + 1;
      if (this._modelInfoRetries <= 10) {
        setTimeout(() => this.renderCurrentVariant(), 100);
      }
    }

    updateModelInfo(variantId) {
      if (!this.modelInfoOverlay) return;
      const model = this.modelInfoMap && this.modelInfoMap[variantId];

      // Match original behaviour: require name + youswim_size only. Other
      // fields gracefully omitted from the rendered sentence when missing.
      if (!model || !model.name || !model.youswim_size) {
        this.modelInfoOverlay.classList.remove('mm-model-info--visible');
        return;
      }

      // Build the sentence from whichever fields are present so a partial
      // metaobject still renders something legible (instead of "is usually
      // a US ,").
      let text = model.name;
      if (model.normal_us && model.cup_size) {
        text += ` is usually a US ${model.normal_us}, ${model.cup_size} cup,`;
      } else if (model.normal_us) {
        text += ` is usually a US ${model.normal_us}`;
      } else if (model.cup_size) {
        text += ` is a ${model.cup_size} cup`;
      }
      text += ` and wears ${model.youswim_size}`;

      this.modelInfoOverlay.textContent = text;
      this.modelInfoOverlay.classList.add('mm-model-info--visible');
      // Text change can alter overlay height — reposition so it stays
      // anchored to the slider's bottom-left.
      this.positionModelInfoOverlay();
    }

    refreshModelInfo() {
      // Called after a product switch via mix-match. The JSON script was
      // already replaced in swapProductData(); the gallery slider was
      // rebuilt. We re-parse, recreate the overlay on the new slider, and
      // re-render against the currently-selected variant. We do NOT re-bind
      // listeners — sectionContainer persists across swaps so the original
      // listeners are still attached.
      this.modelInfoMap = this.parseModelInfoJson();
      if (this.modelInfoOverlay) {
        this.modelInfoOverlay.remove();
        this.modelInfoOverlay = null;
      }
      this.createModelInfoOverlay();
      this.renderCurrentVariant();
    }

    // --- Refresh Judge.me widgets after product switch ---
    refreshJudgeMe() {
      try {
        const productJsonEl = document.querySelector(`#ProductJson-${this.sectionId}`);
        if (!productJsonEl) return;
        const productData = JSON.parse(productJsonEl.textContent);
        const newProductId = productData.id;
        const newHandle = this.state.currentHandle;
        if (!newProductId) return;

        // Update data-id on all Judge.me widgets
        document.querySelectorAll('.jdgm-widget[data-id]').forEach(widget => {
          widget.setAttribute('data-id', newProductId);
        });

        // For the preview badge: fetch fresh badge HTML from the product page
        const badgeWidgets = document.querySelectorAll('.jdgm-preview-badge[data-id]');
        if (badgeWidgets.length && newHandle) {
          fetch('/products/' + newHandle)
            .then(r => r.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newBadge = doc.querySelector('.jdgm-preview-badge');
              if (newBadge) {
                badgeWidgets.forEach(widget => {
                  widget.innerHTML = newBadge.innerHTML;
                  widget.setAttribute('data-id', newProductId);
                });
              }
            })
            .catch(() => {});
        }

        // For the review widget: fetch fresh reviews via Judge.me API and rebuild
        this.refreshJudgeMeReviews(newProductId);
      } catch (e) {
        console.warn('Mix & Match: Could not refresh Judge.me', e);
      }
    }

    // --- Refresh Judge.me reviews widget via API ---
    // --- Reviews widget (delegated) ---
    // The implementation now lives in assets/mm-reviews.js so product
    // templates that never instantiate MixMatch — the static-title sale and
    // style templates, which emit no mix-match-config script — can render the
    // same widget. Previously the widget was blank there while the
    // server-rendered star badge above it still showed.
    //
    // mm-reviews.js is loaded alongside this file by
    // snippets/mix-match-selectors.liquid. It de-duplicates internally:
    // whichever runs first (its own auto-init or this call) renders, and a
    // repeat call for the same product id is a no-op. A product switch passes
    // a new id, so it always re-renders.
    refreshJudgeMeReviews(productId) {
      if (window.MMReviews) window.MMReviews.render(productId);
    }

    // --- Reorder blocks: move description between swatches and size, price above ATC ---
    reorderBlocks() {
      // Use a stable approach: always find elements fresh and insert in correct order
      // Target: description → swatches → price → size → ATC
      const formWrapper = this.sectionContainer.querySelector('[data-form-wrapper]');
      if (!formWrapper) return;

      const selectors = formWrapper.querySelector('.product__selectors');
      if (!selectors) return;

      // Hide original product description (it's replaced by mm-description)
      const origDescription = this.sectionContainer.querySelector('.product__description');
      if (origDescription) {
        origDescription.style.display = 'none';
      }

      // Find the description block (may be anywhere in the section after swap)
      let desc = this.sectionContainer.querySelector('[data-mm-description]');
      if (!desc) {
        desc = this.container.querySelector('[data-mm-description]');
      }

      // Find price block
      const priceBlock = this.sectionContainer.querySelector('.product__price-and-badge');
      if (priceBlock) priceBlock.classList.add('mm-price');

      // Find swatches
      const swatchWrapper = selectors.querySelector('.selector-wrapper--swatches');

      // Build the correct order:
      // [mix-match block] → description → swatches → price → size → ATC
      // Description goes after the mix-match container (between dropdowns and swatches)
      if (desc) {
        // Insert description after the mix-match container, before the form selectors
        const mixMatchBlock = this.container;
        if (mixMatchBlock && mixMatchBlock.parentNode) {
          mixMatchBlock.after(desc);
        }
      }
      if (swatchWrapper) {
        // Insert price after swatches
        if (priceBlock) swatchWrapper.after(priceBlock);
      }

      // Remove any duplicate description blocks
      const allDescs = this.sectionContainer.querySelectorAll('[data-mm-description]');
      if (allDescs.length > 1) {
        for (let i = 1; i < allDescs.length; i++) {
          allDescs[i].remove();
        }
      }
    }

    // --- Sticky ATC bar on mobile ---
    initStickyATC() {
      if (window.innerWidth > 767) return;

      // Create the sticky bar
      const stickyBar = document.createElement('div');
      stickyBar.className = 'mm-sticky-atc';

      const stickyBtn = document.createElement('button');
      stickyBtn.type = 'button';
      stickyBtn.className = 'btn btn--large product__submit__add mm-sticky-atc__btn';
      stickyBtn.textContent = 'Add to Cart';

      // Mirror the page's own sub-line instead of hardcoding a second copy.
      // That line is merchant-editable (theme settings → Cart Drawer V2 →
      // "PDP — text under Add to Cart"), read in snippets/product.liquid, so
      // copying it keeps a single source of truth. assets/mm-sticky-atc.js
      // does the same for the static-title templates.
      //
      // Prefer the data attribute: the GBP store's span carries only
      // .size-subheading on non-sale templates, so a class selector misses it.
      const pageSub = this.sectionContainer.querySelector('[data-atc-subheading]')
        || this.sectionContainer.querySelector('.atc-subheading');
      const stickySub = document.createElement('span');
      stickySub.className = 'atc-subheading' +
        (pageSub && pageSub.classList.contains('atc-subheading--reviews') ? ' atc-subheading--reviews' : '');
      // textContent, not innerHTML — the source is merchant-editable copy.
      stickySub.textContent = pageSub ? pageSub.textContent.trim() : '';

      stickyBar.appendChild(stickyBtn);
      // Merchant cleared the setting -> product.liquid renders no span at all,
      // so the floating button drops its sub-line too rather than showing a
      // stale hardcoded string.
      if (stickySub.textContent) stickyBar.appendChild(stickySub);
      document.body.appendChild(stickyBar);
      this.stickyBar = stickyBar;

      this.stickyBtn = stickyBtn;

      // Wire up the sticky button to trigger the real ATC
      this.stickyBtn.addEventListener('click', () => {
        const realBtn = this.sectionContainer.querySelector('.product__submit__add');
        if (realBtn && !realBtn.disabled) realBtn.click();
      });

      // Sync sticky button state with real ATC
      this.syncStickyBtn();
      // Observe the form wrapper for variant--soldout class changes AND the ATC holder for text changes
      const formWrapper = this.sectionContainer.querySelector('[data-form-wrapper]') || this.sectionContainer;

      this._stickyMutationObserver = new MutationObserver(() => {
        this.syncStickyBtn();
        // Sync again after a tick — theme JS may apply class changes in sequence
        setTimeout(() => this.syncStickyBtn(), 80);
      });
      this._stickyMutationObserver.observe(formWrapper, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'disabled'], characterData: true
      });

      // Track the real ATC button visibility
      const realATC = this.sectionContainer.querySelector('.product__submit__holder');

      let atcVisible = false;
      let scrolledPast50 = false;

      const observer = new IntersectionObserver((entries) => {
        atcVisible = entries[0].isIntersecting;
        this.updateStickyATC(scrolledPast50, atcVisible);
      }, { threshold: 1.0 });
      observer.observe(realATC);

      // Trigger after scrolling past 30% of the viewport height
      const onScroll = () => {
        scrolledPast50 = window.scrollY > window.innerHeight * 0.25;
        this.updateStickyATC(scrolledPast50, atcVisible);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      this._stickyCleanup = () => {
        observer.disconnect();
        window.removeEventListener('scroll', onScroll);
      };
    }

    syncStickyBtn() {
      if (!this.stickyBtn) return;
      const realBtn = this.sectionContainer.querySelector('.product__submit__add');
      if (!realBtn) return;

      // Mirror text and disabled state
      this.stickyBtn.textContent = realBtn.textContent.trim();
      this.stickyBtn.disabled = realBtn.disabled;

      // Check sold-out state from parent class (theme uses .variant--soldout on ancestor)
      const isSoldOut = !!realBtn.closest('.variant--soldout') || !!realBtn.closest('.variant--unavailabe');
      this.stickyBtn.classList.toggle('mm-sticky-atc__btn--sold-out', isSoldOut);

      // Don't use computed styles — use class-based styling instead
      this.stickyBtn.style.removeProperty('background-color');
      this.stickyBtn.style.removeProperty('color');
    }

    // Re-attach sticky ATC observer after DOM swap
    reattachStickyObserver() {
      if (!this.stickyBtn) return;

      // Disconnect old observers
      if (this._stickyMutationObserver) {
        this._stickyMutationObserver.disconnect();
      }
      if (this._stickyCleanup) {
        this._stickyCleanup();
      }

      // Find the new form wrapper and ATC holder (form was replaced)
      const formWrapper = this.sectionContainer.querySelector('[data-form-wrapper]') || this.sectionContainer;
      const realATCHolder = this.sectionContainer.querySelector('.product__submit__holder');
      if (!realATCHolder) return;

      // Re-create MutationObserver on the new form wrapper
      this._stickyMutationObserver = new MutationObserver(() => {
        this.syncStickyBtn();
        setTimeout(() => this.syncStickyBtn(), 80);
      });
      this._stickyMutationObserver.observe(formWrapper, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'disabled'], characterData: true
      });

      // Re-create IntersectionObserver on the new ATC holder
      let atcVisible = false;
      let scrolledPast = false;

      const observer = new IntersectionObserver((entries) => {
        atcVisible = entries[0].isIntersecting;
        this.updateStickyATC(scrolledPast, atcVisible);
      }, { threshold: 1.0 });
      observer.observe(realATCHolder);

      const onScroll = () => {
        scrolledPast = window.scrollY > window.innerHeight * 0.1;
        this.updateStickyATC(scrolledPast, atcVisible);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      this._stickyCleanup = () => {
        observer.disconnect();
        window.removeEventListener('scroll', onScroll);
      };

      // Sync immediately
      this.syncStickyBtn();
    }

    updateStickyATC(scrolledPast, atcVisible) {
      if (!this.stickyBar) return;
      const show = scrolledPast && !atcVisible;
      this.stickyBar.classList.toggle('mm-sticky-atc--visible', show);
    }

    // --- Mark the size selector for custom styling ---
    markSizeSelector() {
      const selectors = this.sectionContainer.querySelectorAll('[data-selector-wrapper]');
      selectors.forEach(wrapper => {
        const legend = wrapper.querySelector('.radio__legend__label');
        if (legend && legend.textContent.trim().toLowerCase().startsWith('size')) {
          wrapper.classList.add('mm-size-selector');
        }
      });
    }

    // --- Group color swatches into labeled rows ---
    groupColorSwatches() {
      const swatchesContainer = this.sectionContainer.querySelector('[data-swatches-container]');
      if (!swatchesContainer) return;

      const buttonsContainer = swatchesContainer.querySelector('[data-variant-buttons]');
      if (!buttonsContainer) return;

      // Skip if already grouped
      if (buttonsContainer.querySelector('.mm-swatch-groups')) return;

      const allButtons = Array.from(buttonsContainer.querySelectorAll('.swatch__button'));
      if (allButtons.length === 0) return;

      const group1 = [];
      const group2 = [];

      allButtons.forEach(btn => {
        const input = btn.querySelector('.swatch__input');
        const colorName = (input ? input.value : '').trim().toLowerCase();
        // Hide colors listed in hidden colors setting
        if (this.hiddenColors.has(colorName)) {
          btn.style.display = 'none';
          return;
        }
        if (this.group2Colors.has(colorName)) {
          group2.push(btn);
        } else {
          group1.push(btn);
        }
      });

      const hasGroups = group2.length > 0;
      const group1Label = this.config.swatchGroup1Label || 'Classics';
      const group2Label = this.config.swatchGroup2Label || 'New';

      const groupsEl = document.createElement('div');
      groupsEl.className = 'mm-swatch-groups';

      const buildRow = (buttons, label, alwaysShowLabel) => {
        const groupEl = document.createElement('div');
        groupEl.className = 'mm-swatch-group';

        if (hasGroups || alwaysShowLabel) {
          const labelEl = document.createElement('span');
          labelEl.className = 'mm-swatch-group-label';
          labelEl.textContent = label;
          groupEl.appendChild(labelEl);
        }

        const rowEl = document.createElement('div');
        rowEl.className = 'mm-swatch-row';
        buttons.forEach(btn => rowEl.appendChild(btn));

        groupEl.appendChild(rowEl);
        return groupEl;
      };

      if (hasGroups) {
        groupsEl.appendChild(buildRow(group2, group2Label, false));
      }
      groupsEl.appendChild(buildRow(group1, group1Label, true));

      // Replace contents
      buttonsContainer.innerHTML = '';
      buttonsContainer.appendChild(groupsEl);

      // Bind change listeners
      buttonsContainer.querySelectorAll('.swatch__input').forEach(input => {
        input.addEventListener('change', () => {
          this.updateInlineColorLabel();
          // Also update the hidden legend span for theme JS compatibility
          const colorDisplay = this.sectionContainer.querySelector('#selected-color-name');
          if (colorDisplay) colorDisplay.textContent = input.value;

          // Scroll to top of gallery on color change
          const gallery = document.querySelector(SELECTORS.gallery + this.sectionId);
          if (gallery) {
            const top = gallery.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        });
      });

      this.updateInlineColorLabel();
    }

    // --- Show selected color name inline next to selected swatch ---
    updateInlineColorLabel() {
      const swatchesContainer = this.sectionContainer.querySelector('[data-swatches-container]');
      if (!swatchesContainer) return;

      // Remove any existing inline label
      const existing = swatchesContainer.querySelector('[data-mm-color-name]');
      if (existing) existing.remove();

      // Find the checked swatch
      const checked = swatchesContainer.querySelector('.swatch__input:checked');
      if (!checked) return;

      const swatchBtn = checked.closest('.swatch__button');
      if (!swatchBtn) return;

      // Create label and insert right after the selected swatch
      const nameEl = document.createElement('span');
      nameEl.className = 'mm-color-name';
      nameEl.setAttribute('data-mm-color-name', '');
      nameEl.textContent = checked.value;

      // Insert after the swatch button (inline in the flow)
      swatchBtn.after(nameEl);

      // Trigger animation by adding visible class on next frame
      requestAnimationFrame(() => {
        nameEl.classList.add('mm-color-name--visible');
      });
    }

    // --- Render pill buttons from config ---
    renderPills() {
      const topOptions = this.container.querySelector('[data-mm-options="top"]');
      const bottomOptions = this.container.querySelector('[data-mm-options="bottom"]');

      if (topOptions) {
        topOptions.innerHTML = '<div class="mix-match__pills">' +
          this.config.tops.map(top => {
            const isActive = top === this.state.currentTop;
            return `<button type="button" class="mix-match__pill${isActive ? ' ' + CLASSES.pillActive : ''}" data-mm-pill-type="top" data-mm-pill-value="${this.escapeHtml(top)}">${this.escapeHtml(top)}</button>`;
          }).join('') +
          '</div>';
      }

      if (bottomOptions) {
        bottomOptions.innerHTML = '<div class="mix-match__pills">' +
          this.config.bottoms.map(bottom => {
            const isActive = bottom === this.state.currentBottom;
            return `<button type="button" class="mix-match__pill${isActive ? ' ' + CLASSES.pillActive : ''}" data-mm-pill-type="bottom" data-mm-pill-value="${this.escapeHtml(bottom)}">${this.escapeHtml(bottom)}</button>`;
          }).join('') +
          '</div>';
      }
    }

    // --- Event binding ---
    bindEvents() {
      // Toggle expand/collapse
      this.container.querySelectorAll(SELECTORS.toggle).forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          const type = toggle.dataset.mmToggle;
          this.toggleOptions(type);
        });
      });

      // Pill button clicks (delegated)
      this.container.addEventListener('click', (e) => {
        const pill = e.target.closest('.mix-match__pill');
        if (!pill || pill.classList.contains(CLASSES.pillUnavailable) || pill.classList.contains(CLASSES.pillActive)) return;

        const type = pill.dataset.mmPillType;
        const value = pill.dataset.mmPillValue;

        if (type === 'top') {
          this.selectTop(value);
          this.collapseOptions('top');
        } else if (type === 'bottom') {
          this.selectBottom(value);
          this.collapseOptions('bottom');
        }
      });

      // Browser back/forward
      window.addEventListener('popstate', (e) => {
        if (e.state && e.state.mixMatch && e.state.handle) {
          this.switchProduct(e.state.handle, false);
        }
      });
    }

    // --- Toggle expand/collapse ---
    toggleOptions(type) {
      const toggle = this.container.querySelector(`[data-mm-toggle="${type}"]`);
      const options = this.container.querySelector(`[data-mm-options="${type}"]`);
      if (!toggle || !options) return;

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', !isExpanded);

      if (isExpanded) {
        options.classList.remove(CLASSES.optionsVisible);
        options.classList.add(CLASSES.optionsHidden);
      } else {
        options.classList.remove(CLASSES.optionsHidden);
        options.classList.add(CLASSES.optionsVisible);
      }
    }

    // --- Collapse options panel ---
    collapseOptions(type) {
      const toggle = this.container.querySelector(`[data-mm-toggle="${type}"]`);
      const options = this.container.querySelector(`[data-mm-options="${type}"]`);
      if (!toggle || !options) return;

      toggle.setAttribute('aria-expanded', 'false');
      options.classList.remove(CLASSES.optionsVisible);
      options.classList.add(CLASSES.optionsHidden);
    }

    // --- Select top style ---
    selectTop(topName) {
      const newBottom = this.findValidBottom(topName, this.state.currentBottom);
      const handle = this.getHandle(topName, newBottom);
      if (!handle) return;

      this.state.currentTop = topName;
      this.state.currentBottom = newBottom;
      this.updateLabels();
      this.updatePillStates();
      this.switchProduct(handle, true);
    }

    // --- Select bottom style ---
    selectBottom(bottomName) {
      const newTop = this.findValidTop(this.state.currentTop, bottomName);
      const handle = this.getHandle(newTop, bottomName);
      if (!handle) return;

      this.state.currentTop = newTop;
      this.state.currentBottom = bottomName;
      this.updateLabels();
      this.updatePillStates();
      this.switchProduct(handle, true);
    }

    // --- Get the color option name from the product JSON ---
    getColorOptionName() {
      if (this._colorOptionName !== undefined) return this._colorOptionName;

      try {
        const productJsonEl = document.querySelector(`#ProductJson-${this.sectionId}`);
        if (productJsonEl) {
          const productData = JSON.parse(productJsonEl.textContent);
          // Color option is typically named "Color" or "Colour"
          const colorOption = productData.options?.find(opt =>
            /^colou?r$/i.test(opt)
          );
          if (colorOption) {
            this._colorOptionName = colorOption;
            return this._colorOptionName;
          }
        }
      } catch (e) {
        // Fall through
      }

      this._colorOptionName = null;
      return null;
    }

    // --- Find valid counterpart when switching ---
    findValidBottom(topName, preferredBottom) {
      const key = topName + '__' + preferredBottom;
      if (this.config.productMap[key]) return preferredBottom;

      // Find first available bottom for this top
      for (const bottom of this.config.bottoms) {
        if (this.config.productMap[topName + '__' + bottom]) return bottom;
      }
      return preferredBottom;
    }

    findValidTop(preferredTop, bottomName) {
      const key = preferredTop + '__' + bottomName;
      if (this.config.productMap[key]) return preferredTop;

      // Find first available top for this bottom
      for (const top of this.config.tops) {
        if (this.config.productMap[top + '__' + bottomName]) return top;
      }
      return preferredTop;
    }

    // --- Get product handle from combo ---
    getHandle(top, bottom) {
      return this.config.productMap[top + '__' + bottom] || null;
    }

    // --- Update toggle labels ---
    updateLabels() {
      const topLabel = this.container.querySelector('[data-mm-label="top"]');
      const bottomLabel = this.container.querySelector('[data-mm-label="bottom"]');
      const suffix = this.container.querySelector('[data-mm-suffix]');

      if (topLabel) topLabel.textContent = this.state.currentTop;
      if (bottomLabel) bottomLabel.textContent = this.state.currentBottom.toLowerCase();

      // Hide "set" suffix for solo pieces and one-pieces
      const bottom = this.state.currentBottom.toLowerCase();
      const isSolo = this.state.currentTop.toLowerCase().includes('only') ||
                     bottom.includes('only');
      const hideSet = isSolo || bottom.includes('one-piece') || bottom.includes('one piece') || bottom.includes('dress') || bottom.includes('romper');
      if (suffix) suffix.style.display = hideSet ? 'none' : '';
      requestAnimationFrame(() => this.checkHelperOverlap());
    }

    // --- Update pill active/unavailable states ---
    updatePillStates() {
      // Top pills
      this.container.querySelectorAll('[data-mm-pill-type="top"]').forEach(pill => {
        const value = pill.dataset.mmPillValue;
        const isActive = value === this.state.currentTop;
        const hasAnyCombo = this.config.bottoms.some(b => this.config.productMap[value + '__' + b]);

        pill.classList.toggle(CLASSES.pillActive, isActive);
        pill.classList.toggle(CLASSES.pillUnavailable, !hasAnyCombo);
      });

      // Bottom pills
      this.container.querySelectorAll('[data-mm-pill-type="bottom"]').forEach(pill => {
        const value = pill.dataset.mmPillValue;
        const isActive = value === this.state.currentBottom;
        const isAvailable = !!this.config.productMap[this.state.currentTop + '__' + value];

        pill.classList.toggle(CLASSES.pillActive, isActive);
        pill.classList.toggle(CLASSES.pillUnavailable, !isAvailable);
      });
    }

    // --- Get currently selected color from the theme's form ---
    getSelectedColor() {
      const optionName = this.getColorOptionName();
      if (!optionName) return null;

      const checked = this.sectionContainer?.querySelector(
        `[data-single-option-selector][name="options[${optionName}]"]:checked`
      );
      return checked?.value || null;
    }

    // --- Restore color selection after product switch ---
    restoreColor(preferredColor) {
      if (!preferredColor) return;

      // Reset cached color option name since form was replaced
      this._colorOptionName = undefined;
      const optionName = this.getColorOptionName();
      if (!optionName) return;

      const formInputs = this.sectionContainer?.querySelectorAll(
        `[data-single-option-selector][name="options[${optionName}]"]`
      );
      if (!formInputs || formInputs.length === 0) return;

      const inputs = Array.from(formInputs);
      let match = inputs.find(input => input.value === preferredColor);

      // If no exact match, fall back to first visible option
      // (Camouflage app hides variants visually but leaves inputs in DOM)
      if (!match) {
        match = inputs.find(input => {
          const swatch = input.closest('.swatch__button') || input.parentElement;
          return swatch && swatch.offsetParent !== null;
        });
      }

      if (match) {
        match.checked = true;
        match.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update the color heading label and re-bind change listeners (lost after DOM swap)
      const colorDisplay = document.getElementById('selected-color-name');
      const activeInput = match || Array.from(formInputs).find(input => input.checked);
      if (colorDisplay && activeInput) {
        colorDisplay.textContent = activeInput.value;
      }
      if (colorDisplay) {
        formInputs.forEach(input => {
          input.addEventListener('change', function () {
            if (this.checked) {
              colorDisplay.textContent = this.value;
            }
          });
        });
      }
    }

    // --- Get size option name ---
    getSizeOptionName() {
      try {
        const productJsonEl = document.querySelector(`#ProductJson-${this.sectionId}`);
        if (productJsonEl) {
          const productData = JSON.parse(productJsonEl.textContent);
          const sizeOption = productData.options?.find(opt =>
            /^size$/i.test(opt)
          );
          if (sizeOption) return sizeOption;
        }
      } catch (e) { /* fall through */ }
      return null;
    }

    // --- Get currently selected size ---
    getSelectedSize() {
      const optionName = this.getSizeOptionName();
      if (!optionName) return null;

      const checked = this.sectionContainer?.querySelector(
        `[data-single-option-selector][name="options[${optionName}]"]:checked`
      );
      return checked?.value || null;
    }

    // --- Restore size selection after product switch ---
    restoreSize(preferredSize) {
      if (!preferredSize) return;

      const optionName = this.getSizeOptionName();
      if (!optionName) return;

      const formInputs = this.sectionContainer?.querySelectorAll(
        `[data-single-option-selector][name="options[${optionName}]"]`
      );
      if (!formInputs || formInputs.length === 0) return;

      // Try exact match first
      let match = Array.from(formInputs).find(input =>
        input.value.toLowerCase() === preferredSize.toLowerCase()
      );

      // If no match, fall back to first visible option
      if (!match) {
        match = Array.from(formInputs).find(input => {
          const wrapper = input.closest('.swatch__button') || input.parentElement;
          return wrapper && wrapper.offsetParent !== null;
        }) || formInputs[0];
      }

      if (match) {
        match.checked = true;
        match.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // --- Switch product via Section Rendering API ---
    async switchProduct(handle, pushState) {
      if (this.state.isFetching || handle === this.state.currentHandle) return;
      this.state.isFetching = true;

      // Capture current color and size before switching
      const previousColor = this.getSelectedColor();
      const previousSize = this.getSelectedSize();

      // Fade out swappable content
      const gallery = document.querySelector(SELECTORS.gallery + this.sectionId);
      const formWrapper = this.sectionContainer.querySelector(SELECTORS.formWrapper);
      const fadeTargets = [gallery, formWrapper].filter(Boolean);
      fadeTargets.forEach(el => {
        el.style.transition = 'opacity 150ms ease';
        el.style.opacity = '0';
      });

      try {
        // Fetch in parallel with fade-out
        const [html] = await Promise.all([
          this.fetchSectionHTML(handle),
          new Promise(r => setTimeout(r, 150)) // wait for fade-out
        ]);

        this.swapContent(html);
        this.state.currentHandle = handle;

        // Update config from the new product's data
        this.updateConfigFromNewProduct(html);

        if (pushState) {
          const newUrl = '/products/' + handle;
          history.pushState({ mixMatch: true, handle: handle }, '', newUrl);
        }

        // Reinitialize theme section JS
        this.reinitSection();

        // Restore color selection, re-mark size selector, regroup swatches, and reorder after reinit
        setTimeout(() => {
          this.restoreColor(previousColor);
          this.restoreSize(previousSize);
          this.markSizeSelector();
          this.groupColorSwatches();
          this.reorderBlocks();
          this.reattachStickyObserver();
          this.refreshModelInfo();
          this.refreshJudgeMe();
          this.syncATCState();
          this.bindATCSync();
          this.checkHelperOverlap();

          // Fade in new content
          const newGallery = document.querySelector(SELECTORS.gallery + this.sectionId);
          const newFormWrapper = this.sectionContainer.querySelector(SELECTORS.formWrapper);
          const fadeInTargets = [newGallery, newFormWrapper].filter(Boolean);
          fadeInTargets.forEach(el => {
            el.style.opacity = '0';
            el.style.transition = 'opacity 200ms ease';
          });
          requestAnimationFrame(() => {
            fadeInTargets.forEach(el => { el.style.opacity = '1'; });
          });
        }, 50);
      } catch (err) {
        console.error('Mix & Match: Failed to switch product', err);
        // Restore opacity on error
        fadeTargets.forEach(el => { el.style.opacity = '1'; });
      } finally {
        this.state.isFetching = false;
        this.showLoading(false);
      }
    }

    // --- Fetch section HTML ---
    async fetchSectionHTML(handle) {
      if (this.cache[handle]) return this.cache[handle];

      // Use the section ID from the template to ensure consistent HTML structure
      const sectionId = this.sectionContainer.dataset.sectionId;
      const url = '/products/' + handle + '?section_id=' + sectionId;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch product: ' + response.status);

      const html = await response.text();
      this.cache[handle] = html;
      return html;
    }

    // --- Swap DOM content ---
    swapContent(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Swap image gallery
      const newGallery = doc.querySelector(SELECTORS.gallery + this.sectionId);
      const oldGallery = document.querySelector(SELECTORS.gallery + this.sectionId);
      if (newGallery && oldGallery) {
        // Destroy Flickity before removing
        this.destroyFlickity(oldGallery);
        oldGallery.replaceWith(newGallery.cloneNode(true));
      }

      // Update description content from new product, then detach before form swap
      const existingDesc = this.sectionContainer.querySelector('[data-mm-description]');
      const newDesc = doc.querySelector('.product__description');
      if (existingDesc && newDesc) {
        existingDesc.innerHTML = newDesc.innerHTML;
      }
      if (existingDesc && existingDesc.parentNode) {
        existingDesc.remove();
      }

      const oldPrice = this.sectionContainer.querySelector(SELECTORS.priceBlock);
      if (oldPrice && oldPrice.parentNode) {
        oldPrice.remove();
      }

      // Update price content from new product
      const newPrice = doc.querySelector(SELECTORS.priceBlock);
      if (newPrice && oldPrice) {
        oldPrice.innerHTML = newPrice.innerHTML;
      }

      // Swap form wrapper (variant selectors + ATC)
      const newForm = doc.querySelector(SELECTORS.formWrapper);
      const oldForm = this.sectionContainer.querySelector(SELECTORS.formWrapper);
      if (newForm && oldForm) {
        oldForm.replaceWith(newForm.cloneNode(true));
      }

      // Re-attach description and price to the section container (reorderBlocks will position them)
      if (existingDesc) {
        this.container.appendChild(existingDesc);
      }
      if (oldPrice) {
        // Append price to section container temporarily; reorderBlocks will move it
        const newFormWrapper = this.sectionContainer.querySelector(SELECTORS.formWrapper);
        if (newFormWrapper) {
          newFormWrapper.parentNode.insertBefore(oldPrice, newFormWrapper);
        } else {
          this.sectionContainer.appendChild(oldPrice);
        }
      }

      // Swap model info JSON
      const newModelInfo = doc.querySelector(SELECTORS.modelInfoJson);
      const oldModelInfo = document.querySelector(SELECTORS.modelInfoJson);
      if (newModelInfo && oldModelInfo) {
        oldModelInfo.replaceWith(newModelInfo.cloneNode(true));
      }

      // Swap product JSON (critical: theme JS reads this for variant IDs)
      const newProductJson = doc.querySelector(`#ProductJson-${this.sectionId}`);
      const oldProductJson = document.querySelector(`#ProductJson-${this.sectionId}`);
      if (newProductJson && oldProductJson) {
        oldProductJson.replaceWith(newProductJson.cloneNode(true));
      }

      // Swap model JSON
      const newModelJson = doc.querySelector(`#ModelJson-${this.sectionId}`);
      const oldModelJson = document.querySelector(`#ModelJson-${this.sectionId}`);
      if (newModelJson && oldModelJson) {
        oldModelJson.replaceWith(newModelJson.cloneNode(true));
      }

      // Swap description accordion content
      // Find the "About this style" accordion and swap its content
      this.swapAccordionContent(doc);
    }

    // --- Swap accordion description ---
    swapAccordionContent(doc) {
      // Swap inner content of each accordion that exists in both old and new HTML
      const oldAccordions = this.sectionContainer.querySelectorAll('.product__accordion__inner');
      const newAccordions = doc.querySelectorAll('.product__accordion__inner');

      oldAccordions.forEach((oldInner, i) => {
        if (newAccordions[i]) {
          oldInner.innerHTML = newAccordions[i].innerHTML;
        }
      });
    }

    // --- Update config state from new product (top/bottom from metafields) ---
    updateConfigFromNewProduct(html) {
      // The new product's config JSON is embedded in the SRA response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newConfigEl = doc.querySelector('[id^="mix-match-config-"]');
      if (newConfigEl) {
        try {
          const newConfig = JSON.parse(newConfigEl.textContent);
          this.state.currentTop = newConfig.currentTop;
          this.state.currentBottom = newConfig.currentBottom;
          this.state.currentHandle = newConfig.currentHandle;
          // Update shared config if needed (tops, bottoms, productMap stay the same)
          this.updateLabels();
          this.updatePillStates();
        } catch (e) {
          // Config update failed, continue with existing state
        }
      }
    }

    // --- Destroy Flickity instance ---
    destroyFlickity(galleryEl) {
      if (!galleryEl) return;
      const slider = galleryEl.querySelector(SELECTORS.mediaSlider);
      if (slider && typeof Flickity !== 'undefined') {
        const flkty = Flickity.data(slider);
        if (flkty) {
          try {
            flkty.destroy();
          } catch (e) {
            // Flickity destroy can throw if already destroyed
          }
        }
      }
    }

    // --- Reinitialize theme section ---
    reinitSection() {
      const instances = window.Shopify?.theme?.sections?.instances;
      const registered = window.Shopify?.theme?.sections?.registered;
      if (!instances || !registered) return;

      const sectionId = this.sectionContainer.dataset.sectionId;

      // Find and unload existing instance
      const instanceIndex = instances.findIndex(inst => inst.id === sectionId);
      if (instanceIndex !== -1) {
        try {
          instances[instanceIndex].onUnload();
        } catch (e) {
          // Unload can throw if components already cleaned up
        }
        instances.splice(instanceIndex, 1);
      }

      // Create new instance by dispatching the section load event
      // The theme listens for shopify:section:load in design mode,
      // but we can manually construct a new Section instance
      const registration = registered['product-template'];
      if (registration) {
        // The Section class is not directly exported, but we can access
        // it through the instances array prototype
        try {
          // Create a minimal Section-like object that calls the registration's callStack
          const container = this.sectionContainer;
          const callStack = registration.getStack();

          const newInstance = {
            container: container,
            id: sectionId,
            type: 'product-template',
            callStack: callStack,
            callFunctions(key, e) {
              this.callStack[key].forEach((func) => {
                const props = {
                  id: this.id,
                  type: this.type,
                  container: this.container,
                };
                if (e) {
                  func.call(props, e);
                } else {
                  func.call(props);
                }
              });
            },
            onLoad() { this.callFunctions('onLoad'); },
            onUnload() { this.callFunctions('onUnload'); },
            onSelect(e) { this.callFunctions('onSelect', e); },
            onDeselect(e) { this.callFunctions('onDeselect', e); },
            onBlockSelect(e) { this.callFunctions('onBlockSelect', e); },
            onBlockDeselect(e) { this.callFunctions('onBlockDeselect', e); },
            onReorder(e) { this.callFunctions('onReorder', e); },
          };

          instances.push(newInstance);
          newInstance.onLoad();
        } catch (e) {
          console.warn('Mix & Match: Failed to reinitialize section', e);
        }
      }
    }

    // --- Bind ATC sync to option changes (fallback for theme event chain) ---
    bindATCSync() {
      // Remove previous listener if any
      if (this._atcSyncHandler) {
        this.sectionContainer.removeEventListener('change', this._atcSyncHandler);
      }
      this._atcSyncHandler = (e) => {
        if (e.target.matches('[data-single-option-selector]')) {
          // Small delay to let theme JS run first, then correct if needed
          setTimeout(() => this.syncATCState(), 10);
        }
      };
      this.sectionContainer.addEventListener('change', this._atcSyncHandler);
    }

    // --- Sync ATC button state with current variant ---
    // Bypasses theme event chain to reliably update ATC after product switch
    syncATCState() {
      const productJsonEl = this.sectionContainer.querySelector('[data-product-json]');
      if (!productJsonEl) return;

      let productData;
      try {
        productData = JSON.parse(productJsonEl.textContent);
      } catch (e) { return; }

      // Read currently selected options from the form
      const form = this.sectionContainer.querySelector('[data-product-form]');
      if (!form) return;

      const formData = new FormData(form);
      const selections = [];
      for (const [key, value] of formData.entries()) {
        if (key.includes('options[')) selections.push(value);
      }

      // Find matching variant
      const variant = productData.variants.find(v =>
        v.options.every((opt, i) => opt === selections[i])
      );

      // Update ATC button
      const atcBtn = this.sectionContainer.querySelector('[data-add-to-cart]');
      const atcText = this.sectionContainer.querySelector('[data-add-to-cart-text]');
      const formWrapper = this.sectionContainer.querySelector('[data-form-wrapper]');
      const buyItNow = this.sectionContainer.querySelector('[data-buy-it-now]');

      if (!atcBtn || !atcText) return;

      if (!variant) {
        // No matching variant
        atcBtn.disabled = true;
        atcText.textContent = theme.strings.unavailable || 'Unavailable';
        formWrapper?.classList.add('variant--unavailabe');
        formWrapper?.classList.remove('variant--soldout');
        buyItNow?.classList.add('hidden');
      } else if (!variant.available) {
        // Sold out
        atcBtn.disabled = true;
        atcText.textContent = theme.strings.sold_out || 'Sold Out';
        formWrapper?.classList.add('variant--soldout');
        formWrapper?.classList.remove('variant--unavailabe');
        buyItNow?.classList.add('hidden');
      } else {
        // Available
        atcBtn.disabled = false;
        atcText.textContent = theme.strings.add_to_cart || 'Add to Cart';
        formWrapper?.classList.remove('variant--soldout', 'variant--unavailabe');
        buyItNow?.classList.remove('hidden');
      }
    }

    // --- Hide helper text if it overlaps with the title ---
    checkHelperOverlap() {
      this.container.querySelectorAll('.mix-match__row').forEach(row => {
        const toggle = row.querySelector('.mix-match__toggle');
        const helper = row.querySelector('.mix-match__helper');
        if (!toggle || !helper) return;

        // Remove hidden class first so we can measure
        row.classList.remove('mix-match__row--helper-hidden');

        // Temporarily make helper visible but invisible to measure
        helper.style.visibility = 'hidden';
        helper.style.opacity = '1';

        const toggleRect = toggle.getBoundingClientRect();
        const helperRect = helper.getBoundingClientRect();

        // Check if the toggle's right edge overlaps the helper's left edge
        const overlaps = toggleRect.right > helperRect.left - 16;

        helper.style.visibility = '';
        helper.style.opacity = '';

        if (overlaps) {
          row.classList.add('mix-match__row--helper-hidden');
        }
      });
    }

    // --- Loading state ---
    showLoading(show) {
      const loading = this.container.querySelector(SELECTORS.loading);
      if (loading) {
        loading.classList.toggle(CLASSES.loadingActive, show);
      }

      // Add/remove switching class for content fade
      if (this.sectionContainer) {
        this.sectionContainer.classList.toggle(CLASSES.switching, show);
      }
    }


    // --- Utility: escape HTML ---
    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  // --- Initialize on DOM ready ---
  function initMixMatch() {
    const containers = document.querySelectorAll(SELECTORS.container);
    containers.forEach(container => {
      if (!container._mixMatchInstance) {
        container._mixMatchInstance = new MixMatch(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMixMatch);
  } else {
    initMixMatch();
  }

  // Reinitialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', function () {
    setTimeout(initMixMatch, 100);
  });
})();
